import React, { useState } from "react";
import styled from "@emotion/styled";
import { Typography, TableRow, TableCell, Tooltip } from "@mui/material";
import { RunCircle, WarningAmber, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import TaskIcon from '@mui/icons-material/Task';
import CommentIcon from '@mui/icons-material/Comment';
import UserMenuChip from "./UserMenuChip";
import UserAssignmentDialog from "./UserAssignmentDialog";
import { Link } from "react-router-dom";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsLocked } from "../utils/lockUtils";
import { addUpTime, convertMinutesToHours } from "../utils/timeUtils";
import { formatDate } from "../utils/dateUtils";

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: "bold",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const ProjectNumber = styled(Typography)(({ theme }) => ({
    fontFamily: "monospace",
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.disabled,
    marginRight: theme.spacing(0.75),
}));

const NameCell = styled(TableCell)({
    width: 'auto',
    maxWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const StatusBox = styled("div")(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.25),
}));

const NumericCell = styled(TableCell)({
    textAlign: 'right',
});

const NumericContent = styled("div")({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
});

const IconsBox = styled("div")(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    alignItems: 'center',
}));

const IconItem = styled("div")({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
});

const DoubleArrowUp = styled(KeyboardArrowUp)(({ theme }) => ({
    marginBottom: theme.spacing(-2.25),
    marginTop: theme.spacing(-0.5),
}));

const DoubleArrowDown = styled(KeyboardArrowDown)(({ theme }) => ({
    marginBottom: theme.spacing(-2.25),
    marginTop: theme.spacing(-0.5),
}));

const PriorityBox = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
});

const getPriorityIcon = (priority) => {
    switch (priority) {
        case "Highest":
            return <Tooltip title="Highest Priority"><PriorityBox><DoubleArrowUp color="error" /><KeyboardArrowUp color="error" /></PriorityBox></Tooltip>;
        case "High":
            return <Tooltip title="High Priority"><KeyboardArrowUp color="warning" /></Tooltip>;
        case "Low":
            return <Tooltip title="Low Priority"><KeyboardArrowDown color="primary" /></Tooltip>;
        case "Lowest":
            return <Tooltip title="Lowest Priority"><PriorityBox><DoubleArrowDown color="primary" /><KeyboardArrowDown color="primary" /></PriorityBox></Tooltip>;
        default:
            return null;
    }
};


const AccountTabProjectsList = ({ project }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const hasPermission = useHasPermission();
    const canAssignProject = hasPermission("assignProject");
    const canSeeInternal = hasPermission("canSeeInternal");
    const isLocked = useIsLocked(project?.account, project?.account?.client);

    const handleOpenDialog = () => setIsDialogOpen(true);
    const handleCloseDialog = () => setIsDialogOpen(false);

    const actualHours = addUpTime(convertMinutesToHours(project.time?.total)).toFixed(2);
    const quotedHours = (project.budget?.quoted || 0).toFixed(2);
    const budgetedHours = (project.budget?.budgeted || 0).toFixed(2);

    return (
        <TableRow hover>
            <TableCell padding="none">
                <StatusBox>
                    {project.rush && <Tooltip title="Rush"><RunCircle color="error" fontSize="small" /></Tooltip>}
                    {getPriorityIcon(project.priority)}
                    {project.remainingDays === 'Overdue' && <Tooltip title="Project is Overdue"><WarningAmber color="warning" fontSize="small" /></Tooltip>}
                </StatusBox>
            </TableCell>

            <NameCell>
                <Tooltip title={project.name} arrow>
                    <ProjectName variant="body2" component={Link} to={`/account/${project.account.slug}/project/${project._id}`}>
                        {project.name}
                    </ProjectName>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                    {project.projectId ? <ProjectNumber component="span">Project #{project.projectId} -</ProjectNumber> : null}
                    {formatDate(project.startDate)} – {formatDate(project.dueDate)}
                </Typography>
            </NameCell>

            {canSeeInternal && (
                <NumericCell>
                    <NumericContent>
                        <Typography variant="caption" color="text.secondary">Budgeted</Typography>
                        <Typography variant="body2">{budgetedHours}</Typography>
                    </NumericContent>
                </NumericCell>
            )}

            <NumericCell>
                <NumericContent>
                    <Typography variant="caption" color="text.secondary">Quoted</Typography>
                    <Typography variant="body2">{quotedHours}</Typography>
                </NumericContent>
            </NumericCell>

            <NumericCell>
                <NumericContent>
                    <Typography variant="caption" color="text.secondary">Actual</Typography>
                    <Typography variant="body2" color={parseFloat(actualHours) > parseFloat(quotedHours) ? "error" : "success.main"}>
                        {actualHours}
                    </Typography>
                </NumericContent>
            </NumericCell>

            <TableCell>
                <UserMenuChip user={project.responsible} size="small" onReAssign={canAssignProject && !isLocked ? handleOpenDialog : undefined} menuItems={{ reAssign: canAssignProject && !isLocked }} />
            </TableCell>

            <TableCell align="right">
                <IconsBox>
                    <Tooltip title="Tasks">
                        <IconItem>
                            <TaskIcon fontSize="small" />
                            <Typography variant="caption">{project.taskNumber}</Typography>
                        </IconItem>
                    </Tooltip>
                    <Tooltip title="Comments">
                        <IconItem>
                            <CommentIcon fontSize="small" />
                            <Typography variant="caption">{project.commentNumber}</Typography>
                        </IconItem>
                    </Tooltip>
                </IconsBox>
                <UserAssignmentDialog
                    isOpen={isDialogOpen}
                    onClose={handleCloseDialog}
                    objectId={project._id}
                    objectType="project"
                    accountId={project.account?._id || project.account}
                    singleSelection={true}
                />
            </TableCell>
        </TableRow>
    );
};

export default AccountTabProjectsList;
